import { getAuthHeaders } from './authActions'

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001'
const EXPERIENCE_FIELD_MAX_LENGTH = 55

const parseErrorMessage = async (res, fallbackMessage) => {
	const text = await res.text()
	if (!text) return fallbackMessage

	try {
		const parsed = JSON.parse(text)
		return parsed?.message || fallbackMessage
	} catch {
		return text
	}
}

export const getExperiencePositions = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/positions`,
		{
			headers: { ...getAuthHeaders() },
		}
	)
	if (!res.ok) throw new Error('Failed to fetch positions')
	return res.json()
}

export const getExperiencePosition = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/position`,
		{
			headers: { ...getAuthHeaders() },
		}
	)

	if (!res.ok) throw new Error('Failed to fetch user position')
	return res.json()
}

export const updateStaffPosition = async (
	experienceSlug,
	orgSlug,
	positionId,
	{ level, station, positionName }
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/positions/${positionId}`,
		{
			method: 'PATCH',
			headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({ level, station, positionName }),
		}
	)
	if (!res.ok) throw new Error('Failed to update position')
	return res.json()
}

export const addStaffPosition = async ({
	orgSlug,
	experienceSlug,
	userId,
	level,
	station,
	positionName,
}) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/positions`,
		{
			method: 'POST',
			headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
			body: JSON.stringify({ userId, level, station, positionName }),
		}
	)
	if (!res.ok) throw new Error('Failed to add position')
	return res.json()
}

export const createExperience = async ({
	orgSlug,
	name,
	slug,
	shortName,
	primaryColor,
	textColor,
	labelColor,
	stripImage,
	timezone,
	startDate,
	endDate,
	locationId,
}) => {
	const normalizedName = String(name || '').trim()
	const normalizedSlug = String(slug || '').trim().toLowerCase()

	if (!normalizedName) {
		throw new Error('Experience name cannot be empty')
	}
	if (!normalizedSlug) {
		throw new Error('Slug cannot be empty')
	}
	if (normalizedName.length > EXPERIENCE_FIELD_MAX_LENGTH) {
		throw new Error(
			`Experience name must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer`
		)
	}
	if (normalizedSlug.length > EXPERIENCE_FIELD_MAX_LENGTH) {
		throw new Error(
			`Slug must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer`
		)
	}
	if (!orgSlug) {
		throw new Error('Organization slug is required to create an experience')
	}

	const res = await fetch(`${BASE_URL}/api/${orgSlug}/experiences`, {
		method: 'POST',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			name: normalizedName,
			slug: normalizedSlug,
			shortName,
			primaryColor,
			textColor,
			labelColor,
			stripImage,
			timezone,
			startDate,
			endDate,
			locationId,
		}),
	})

	if (!res.ok) {
		throw new Error(await parseErrorMessage(res, 'Failed to create experience'))
	}

	return res.json()
}

export const duplicateExperience = async ({
	orgSlug,
	experienceSlug,
	name,
	slug,
}) => {
	const normalizedName = String(name || '').trim()
	const normalizedSlug = String(slug || '').trim().toLowerCase()

	if (!normalizedName) {
		throw new Error('Experience name cannot be empty')
	}
	if (!normalizedSlug) {
		throw new Error('Slug cannot be empty')
	}
	if (normalizedName.length > EXPERIENCE_FIELD_MAX_LENGTH) {
		throw new Error(
			`Experience name must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer`
		)
	}
	if (normalizedSlug.length > EXPERIENCE_FIELD_MAX_LENGTH) {
		throw new Error(
			`Slug must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer`
		)
	}
	if (!orgSlug || !experienceSlug) {
		throw new Error('Source experience is required to create a copy')
	}

	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/duplicate`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				name: normalizedName,
				slug: normalizedSlug,
			}),
		}
	)

	if (!res.ok) {
		throw new Error(
			await parseErrorMessage(res, 'Failed to duplicate experience')
		)
	}

	return res.json()
}

export const getExperience = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}`,
		{
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to fetch experience')
	return res.json()
}

export const getOrgLocations = async (orgSlug) => {
	const res = await fetch(`${BASE_URL}/api/${orgSlug}/org/locations`, {
		headers: getAuthHeaders(),
	})
	if (!res.ok) throw new Error('Failed to fetch locations')
	return res.json()
}

export const createOrgLocation = async (orgSlug, name) => {
	const normalizedName = String(name || '').trim()
	if (!normalizedName) {
		throw new Error('Location name is required')
	}

	const res = await fetch(`${BASE_URL}/api/${orgSlug}/org/locations`, {
		method: 'POST',
		headers: {
			...getAuthHeaders(),
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({ name: normalizedName }),
	})

	if (!res.ok) {
		throw new Error(await parseErrorMessage(res, 'Failed to create location'))
	}
	return res.json()
}

export const getItemOptions = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/item-options`,
		{
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to fetch item options')
	return res.json()
}

export const getStationOptions = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/station-options`,
		{
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to fetch station options')
	return res.json()
}

export const getSegmentOptions = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/segment-options`,
		{
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to fetch segment options')
	return res.json()
}

export const updateExperience = async (experienceSlug, orgSlug, data) => {
	const payload = { ...(data || {}) }

	if (payload.name !== undefined) {
		payload.name = String(payload.name).trim()
		if (!payload.name) {
			throw new Error('Experience name cannot be empty')
		}
		if (payload.name.length > EXPERIENCE_FIELD_MAX_LENGTH) {
			throw new Error(
				`Experience name must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer`
			)
		}
	}

	if (payload.slug !== undefined) {
		payload.slug = String(payload.slug).trim().toLowerCase()
		if (!payload.slug) {
			throw new Error('Slug cannot be empty')
		}
		if (payload.slug.length > EXPERIENCE_FIELD_MAX_LENGTH) {
			throw new Error(
				`Slug must be ${EXPERIENCE_FIELD_MAX_LENGTH} characters or fewer`
			)
		}
	}

	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}`,
		{
			method: 'PATCH',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(payload),
		}
	)
	if (!res.ok) {
		throw new Error(await parseErrorMessage(res, 'Failed to update experience'))
	}
	return res.json()
}

export const addItemOption = async (experienceSlug, orgSlug, data) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/item-options`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) {
		throw new Error(await parseErrorMessage(res, 'Failed to create item option'))
	}
	return res.json()
}

export const updateItemOption = async (
	experienceSlug,
	orgSlug,
	itemId,
	data
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/item-options/${itemId}`,
		{
			method: 'PATCH',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) {
		throw new Error(await parseErrorMessage(res, 'Failed to update item option'))
	}
	return res.json()
}

export const updateStationOption = async (
	experienceSlug,
	orgSlug,
	stationId,
	data
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/station-options/${stationId}`,
		{
			method: 'PATCH',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) {
		throw new Error(
			await parseErrorMessage(res, 'Failed to update station option')
		)
	}
	return res.json()
}

export const updateSegmentOption = async (
	experienceSlug,
	orgSlug,
	segmentId,
	data
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/segment-options/${segmentId}`,
		{
			method: 'PATCH',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) {
		throw new Error(
			await parseErrorMessage(res, 'Failed to update segment option')
		)
	}
	return res.json()
}

export const deleteItemOption = async (experienceSlug, orgSlug, itemId) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/item-options/${itemId}`,
		{
			method: 'DELETE',
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to delete item option')
	return res.json()
}

export const addStationOption = async (experienceSlug, orgSlug, data) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/station-options`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) {
		throw new Error(
			await parseErrorMessage(res, 'Failed to create station option')
		)
	}
	return res.json()
}

export const addSegmentOption = async (experienceSlug, orgSlug, data) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/segment-options`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) {
		throw new Error(
			await parseErrorMessage(res, 'Failed to create segment option')
		)
	}
	return res.json()
}

export const deleteStationOption = async (
	experienceSlug,
	orgSlug,
	stationId
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/station-options/${stationId}`,
		{
			method: 'DELETE',
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to delete station option')
	return res.json()
}

export const deleteSegmentOption = async (
	experienceSlug,
	orgSlug,
	segmentId
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/segment-options/${segmentId}`,
		{
			method: 'DELETE',
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to delete segment option')
	return res.json()
}

export const deleteStaffPosition = async (
	experienceSlug,
	orgSlug,
	positionId
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/positions/${positionId}`,
		{
			method: 'DELETE',
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to delete staff position')
	return res.json()
}

export const getExperienceStats = async (experienceSlug, orgSlug) => {
	try {
		const res = await fetch(
			`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/stats`,
			{
				method: 'GET',
				headers: {
					'Content-Type': 'application/json',
					...getAuthHeaders(),
				},
			}
		)

		if (!res.ok) {
			throw new Error(`Failed to fetch stats: ${res.status}`)
		}

		const data = await res.json()
		return data
	} catch (err) {
		console.error('❌ Error in getExperienceStats:', err)
		throw err
	}
}

export const getExperienceCheckInProgress = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/check-in-progress`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
		},
	)

	if (!res.ok) {
		throw new Error(`Failed to fetch check-in progress: ${res.status}`)
	}

	return res.json()
}

export const getExperienceSales = async (experienceSlug, orgSlug, query = {}) => {
	const searchParams = new URLSearchParams()
	Object.entries(query).forEach(([key, value]) => {
		if (value !== '' && value !== null && value !== undefined) {
			searchParams.set(key, String(value))
		}
	})
	const queryString = searchParams.toString()
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/sales${
			queryString ? `?${queryString}` : ''
		}`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				...getAuthHeaders(),
			},
		}
	)

	if (!res.ok) {
		throw new Error(`Failed to fetch sales: ${res.status}`)
	}

	return res.json()
}

export const getMessageSetups = async (experienceSlug, orgSlug) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/message-setups`,
		{
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to fetch message setups')
	return res.json()
}

export const addMessageSetup = async (orgSlug, experienceSlug, data) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/message-setups`,
		{
			method: 'POST',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) throw new Error('Failed to create message setup')
	return res.json()
}

export const updateMessageSetup = async (
	orgSlug,
	experienceSlug,
	messageId,
	data
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/message-setups/${messageId}`,
		{
			method: 'PATCH',
			headers: {
				...getAuthHeaders(),
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		}
	)
	if (!res.ok) throw new Error('Failed to update message setup')
	return res.json()
}

export const deleteMessageSetup = async (
	experienceSlug,
	orgSlug,
	messageId
) => {
	const res = await fetch(
		`${BASE_URL}/api/${orgSlug}/experiences/${experienceSlug}/message-setups/${messageId}`,
		{
			method: 'DELETE',
			headers: getAuthHeaders(),
		}
	)
	if (!res.ok) throw new Error('Failed to delete message setup')
	return res.json()
}
